import React, { useMemo, useState } from 'react';
import { AddressProps } from '@partsbadger/library';
import { SalesOrderProps } from '../../../types/types';
import ContactStore from '../../../stores/ContactStore';
import ShowDefault from './ShowDefault';
import AddAddressModal from './AddAddressModal';
import { observer } from 'mobx-react';

interface IProps {
    type: 'Billing' | 'Shipping' | 'Billing and Shipping';
    order: SalesOrderProps;
    hideEditButton?: boolean;
}

function buildAddress(
    type: IProps['type'],
    name?: string,
    street?: string,
    city?: string,
    state?: string,
    country?: any,
    zip_code?: string
): AddressProps {
    return {
        name: name || '',
        type,
        street: street || '',
        city: city || '',
        state: state || '',
        country: { alpha3: '', code: country?.code ?? '', name: country?.name ?? '' },
        zip_code: zip_code || '',
        primary: true,
    };
}

const NewAddresses: React.FC<IProps> = ({ order, type, hideEditButton }) => {
    const { billingAddresses, shippingAddresses, default_shipping_address, default_billing_address } = ContactStore;

    const billingAddress = order.billing_street
        ? buildAddress(
              type,
              order.billing_name,
              order.billing_street,
              order.billing_city,
              order.billing_state,
              order.billing_country,
              order.billing_zip_code
          )
        : default_billing_address?.street
        ? { ...default_billing_address }
        : null;

    const shippingAddress = order.shipping_street
        ? buildAddress(
              type,
              order.shipping_name,
              order.shipping_street,
              order.shipping_city,
              order.shipping_state,
              order.shipping_country,
              order.shipping_zip_code
          )
        : default_shipping_address?.street
        ? { ...default_shipping_address }
        : null;

    const currentAddress = useMemo(() => (type === 'Billing' ? billingAddress : shippingAddress), [
        type,
        billingAddress,
        shippingAddress,
    ]);

    const addresses = useMemo(() => (type === 'Billing' ? billingAddresses : shippingAddresses), [
        type,
        billingAddresses,
        shippingAddresses,
    ]);

    const [isAddOpen, setIsAddOpen] = useState(false);

    return (
        <>
            <ShowDefault
                currentAddress={currentAddress}
                addresses={addresses}
                type={type}
                hideEditButton={hideEditButton}
                onAddNew={() => setIsAddOpen(true)}
            />

            <AddAddressModal
                open={isAddOpen}
                type={type}
                onClose={() => setIsAddOpen(false)}
                initial={currentAddress ?? null}
            />
        </>
    );
};

export default observer(NewAddresses);
